import { buildGraph } from './graph';
import type { Edges, NodeId } from './types';
import { addDays, daysBetween, isOverdue, isoDayKey, type ProjectRow, type Todo } from './todos';

/**
 * THE PROJECT MODEL (V0.50.2) — what the dashboard's Projects tab computes, and nothing else does.
 *
 * A project is a node marked as one (`ProjectRow`) plus its SUBTREE plus the to-dos on all of
 * them. The server ships the facts — `GET /api/projects` returns the marks the reader may see
 * and every to-do inside their subtrees — and THIS file turns them into every figure on the
 * tab: the count per node rolled up from the leaves, the project's percentage and status, who
 * carries how much, and the burn-up. Pure, so `projects.test.ts` can pin every rule and the e2e
 * suite can recompute a screen's figures from the wire (`20-dashboard`'s idiom).
 *
 * TODAY IS A PARAMETER everywhere (the rule `lib/health.ts` states): nothing here reads the
 * clock. Days are `YYYY-MM-DD` strings compared as strings — `lib/todos.ts`'s header.
 *
 * THE SCOPE IS THE SUBTREE, and a nested project is a milestone inside its parent: its to-dos
 * count in both, by design. The tree comes from the map the client already holds (`edges`),
 * which is also why nothing about the scope is ever shipped or stored.
 *
 * `scopeTree` is its own pre-order walk rather than `GraphIndex.descendants()`, whose stack
 * pops siblings in REVERSE — right for the subtree drag it was written for, wrong for a table
 * that must list children in the map's order.
 */

/** A to-do due within this many days of today is "due soon". */
export const DUE_SOON_DAYS = 7;
/** How many percentage points the work may trail the calendar before a project reads "at risk". */
export const SLACK_POINTS = 15;

/* ------------------------------------------------------------------------------ counts */

/**
 * DISJOINT buckets over one list of to-dos. `overdue` and `dueSoon` are both OPEN and split by
 * the due date; `unassigned` is OPEN with nobody named (finished work needs nobody).
 */
export interface Counts {
  total: number;
  open: number;
  done: number;
  overdue: number;
  dueSoon: number;
  unassigned: number;
}

export const EMPTY_COUNTS: Readonly<Counts> = Object.freeze({
  total: 0,
  open: 0,
  done: 0,
  overdue: 0,
  dueSoon: 0,
  unassigned: 0,
});

/** `'u:<id>'` for a person, `'g:<id>'` for a team — the keys every per-assignee figure uses. */
export const assigneeKeysOf = (t: Pick<Todo, 'assigneeUserIds' | 'assigneeGroupIds'>): string[] => [
  ...t.assigneeUserIds.map((id) => `u:${id}`),
  ...t.assigneeGroupIds.map((id) => `g:${id}`),
];

export function countTodos(todos: readonly Todo[], today: string): Counts {
  const soonUntil = addDays(today, DUE_SOON_DAYS);
  const c: Counts = { ...EMPTY_COUNTS };
  for (const t of todos) {
    c.total += 1;
    if (t.state === 'done') {
      c.done += 1;
      continue;
    }
    c.open += 1;
    if (t.dueDate) {
      if (t.dueDate < today) c.overdue += 1;
      else if (t.dueDate <= soonUntil) c.dueSoon += 1;
    }
    if (!assigneeKeysOf(t).length) c.unassigned += 1;
  }
  return c;
}

export function addCounts(a: Counts, b: Counts): Counts {
  return {
    total: a.total + b.total,
    open: a.open + b.open,
    done: a.done + b.done,
    overdue: a.overdue + b.overdue,
    dueSoon: a.dueSoon + b.dueSoon,
    unassigned: a.unassigned + b.unassigned,
  };
}

/** Whole percent done; 0 for nothing planned (a ring reads it, so it is an integer). */
export const pctOf = (c: Counts): number => (c.total ? Math.round((c.done / c.total) * 100) : 0);

/* ----------------------------------------------------------------------------- due dates */

export type DueTone = 'bad' | 'warn' | 'none';

/** Overdue reads `bad`, due within `DUE_SOON_DAYS` reads `warn`, done or undated reads `none`. */
export function dueTone(due: string | null, state: string, today: string): DueTone {
  if (!due || state === 'done') return 'none';
  if (isOverdue(due, state, today)) return 'bad';
  return due <= addDays(today, DUE_SOON_DAYS) ? 'warn' : 'none';
}

/** The earliest due date among the OPEN to-dos, overdue ones included, or null. */
export function nextDueOf(todos: readonly Todo[]): string | null {
  let best: string | null = null;
  for (const t of todos) {
    if (t.state === 'done' || !t.dueDate) continue;
    if (best === null || t.dueDate < best) best = t.dueDate;
  }
  return best;
}

const minDay = (a: string | null, b: string | null): string | null =>
  a === null ? b : b === null ? a : a < b ? a : b;

/* ---------------------------------------------------------------------------- the scope */

/** The project node first, then every descendant in PRE-ORDER, children in the map's order. */
export function scopeTree(edges: Edges, root: NodeId): { nodeId: NodeId; depth: number }[] {
  const g = buildGraph(edges);
  const out: { nodeId: NodeId; depth: number }[] = [];
  const seen = new Set<NodeId>();
  const walk = (id: NodeId, depth: number) => {
    if (seen.has(id)) return; // a cycle in `edges` should degrade, not hang the tab
    seen.add(id);
    out.push({ nodeId: id, depth });
    for (const kid of g.childrenOf(id)) walk(kid, depth + 1);
  };
  walk(root, 0);
  return out;
}

export function scopeSet(edges: Edges, root: NodeId): ReadonlySet<NodeId> {
  return new Set(scopeTree(edges, root).map((r) => r.nodeId));
}

export function groupByNode(todos: readonly Todo[]): Map<NodeId, Todo[]> {
  const m = new Map<NodeId, Todo[]>();
  for (const t of todos) {
    const list = m.get(t.nodeId);
    if (list) list.push(t);
    else m.set(t.nodeId, [t]);
  }
  return m;
}

/* ------------------------------------------------------------------------------ rollup */

export interface NodeRollup {
  nodeId: NodeId;
  /** The to-dos ON this node. */
  own: Counts;
  /** This node and everything beneath it. */
  subtree: Counts;
  /** Earliest due date among the OPEN to-dos in the subtree, or null. */
  nextDue: string | null;
  /** `'u:<id>'` / `'g:<id>'` over the subtree, unique and sorted. */
  assigneeKeys: string[];
}

/**
 * Every node in the scope with its own and rolled-up figures. Post-order: a node's `subtree`
 * is its `own` plus the sum of its children's `subtree`s, so a task on a grandchild counts in
 * its parent and in the project. To-dos on nodes outside the scope are ignored.
 */
export function rollup(edges: Edges, root: NodeId, todos: readonly Todo[], today: string): Map<NodeId, NodeRollup> {
  const g = buildGraph(edges);
  const byNode = groupByNode(todos);
  const out = new Map<NodeId, NodeRollup>();
  const seen = new Set<NodeId>();
  const walk = (id: NodeId): NodeRollup => {
    seen.add(id);
    const ownTodos = byNode.get(id) ?? [];
    const own = countTodos(ownTodos, today);
    let subtree = own;
    let nextDue = nextDueOf(ownTodos);
    const keys = new Set<string>(ownTodos.flatMap(assigneeKeysOf));
    for (const kid of g.childrenOf(id)) {
      if (seen.has(kid)) continue;
      const r = walk(kid);
      subtree = addCounts(subtree, r.subtree);
      nextDue = minDay(nextDue, r.nextDue);
      for (const k of r.assigneeKeys) keys.add(k);
    }
    const r: NodeRollup = { nodeId: id, own, subtree, nextDue, assigneeKeys: [...keys].sort() };
    out.set(id, r);
    return r;
  };
  walk(root);
  return out;
}

/* --------------------------------------------------------------------------- the summary */

export type ProjectStatus = 'not-started' | 'on-track' | 'at-risk' | 'late' | 'done';

export interface ProjectSummary {
  nodeId: NodeId;
  counts: Counts;
  pct: number;
  startDate: string | null;
  dueDate: string | null;
  /** How far through the calendar the project is, 0..100 — only when BOTH dates exist. */
  elapsedPct: number | null;
  /** Days from today to the target; negative once it has passed; null without a target. */
  daysLeft: number | null;
  nextDue: string | null;
  status: ProjectStatus;
}

/**
 * Time elapsed between start and target, clamped to 0..100. Null unless both dates exist. A
 * one-day project (start = target) reads 0 before that day and 100 from it.
 */
export function elapsedPct(start: string | null, due: string | null, today: string): number | null {
  if (!start || !due || due < start) return null;
  if (start === due) return today < start ? 0 : 100;
  const pct = Math.round((daysBetween(start, today) / daysBetween(start, due)) * 100);
  return Math.max(0, Math.min(100, pct));
}

/**
 * THE STATUS RULE, in this order — each line is a test in `projects.test.ts`:
 *   done        — something was planned and nothing is open;
 *   late        — the target has passed (a missed target is late whatever the list says);
 *   at-risk     — something is overdue;
 *   not-started — nothing planned, or the start is still ahead and nothing is done;
 *   at-risk     — the work trails the calendar by more than SLACK_POINTS (both dates only);
 *   on-track    — otherwise.
 * Never infers a start from `createdAt`: a guess presented as a judgement. The burn-up's axis may.
 */
export function projectStatus(
  c: Counts,
  plan: Pick<ProjectRow, 'startDate' | 'dueDate'>,
  elapsed: number | null,
  today: string,
): ProjectStatus {
  if (c.total > 0 && c.open === 0) return 'done';
  if (plan.dueDate && plan.dueDate < today) return 'late';
  if (c.overdue > 0) return 'at-risk';
  if (c.total === 0 || (plan.startDate && plan.startDate > today && c.done === 0)) return 'not-started';
  if (elapsed !== null && pctOf(c) + SLACK_POINTS < elapsed) return 'at-risk';
  return 'on-track';
}

export function projectSummary(project: ProjectRow, scopeTodos: readonly Todo[], today: string): ProjectSummary {
  const counts = countTodos(scopeTodos, today);
  const elapsed = elapsedPct(project.startDate, project.dueDate, today);
  return {
    nodeId: project.nodeId,
    counts,
    pct: pctOf(counts),
    startDate: project.startDate,
    dueDate: project.dueDate,
    elapsedPct: elapsed,
    daysLeft: project.dueDate ? daysBetween(today, project.dueDate) : null,
    nextDue: nextDueOf(scopeTodos),
    status: projectStatus(counts, project, elapsed, today),
  };
}

/* --------------------------------------------------------------------------- assignees */

export type AssigneeKind = 'user' | 'group' | 'unassigned';

export interface AssigneeRow {
  /** `'u:<id>'`, `'g:<id>'`, or `'unassigned'`. */
  key: string;
  kind: AssigneeKind;
  id: string | null;
  counts: Counts;
  nextDue: string | null;
}

/**
 * One row per person or team named on at least one to-do, plus `unassigned` for the to-dos
 * that name nobody. A to-do with several assignees counts once per assignee. GROUPS ARE NOT
 * EXPANDED to their members: `DirectoryGroup.memberIds` exists, but a team row is a team row —
 * who is reached by it is the server's answer (`/api/me/todos`), not this table's. Sorted by
 * open work first, then overdue, then key; `unassigned` always last.
 */
export function assigneeMetrics(todos: readonly Todo[], today: string): AssigneeRow[] {
  const buckets = new Map<string, Todo[]>();
  const put = (key: string, t: Todo) => {
    const list = buckets.get(key);
    if (list) list.push(t);
    else buckets.set(key, [t]);
  };
  for (const t of todos) {
    const keys = assigneeKeysOf(t);
    if (!keys.length) put('unassigned', t);
    for (const k of keys) put(k, t);
  }
  const rows: AssigneeRow[] = [...buckets.entries()].map(([key, list]) => ({
    key,
    kind: key === 'unassigned' ? 'unassigned' : key.startsWith('g:') ? 'group' : 'user',
    id: key === 'unassigned' ? null : key.slice(2),
    counts: countTodos(list, today),
    nextDue: nextDueOf(list),
  }));
  return rows.sort(
    (a, b) =>
      Number(a.kind === 'unassigned') - Number(b.kind === 'unassigned') ||
      b.counts.open - a.counts.open ||
      b.counts.overdue - a.counts.overdue ||
      (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );
}

/* ----------------------------------------------------------------------------- burn-up */

export interface BurnupPoint {
  day: string;
  /** To-dos that existed by the end of this day. */
  created: number;
  /** To-dos done by the end of this day. */
  completed: number;
}

/**
 * The days the chart spans: from the start date or the first to-do, whichever is earlier, to
 * today or the target, whichever is later. Null with nothing to place. The FROM may infer
 * itself from `createdAt` — this is an axis, not a judgement (compare `projectStatus`).
 */
export function burnupRange(
  project: Pick<ProjectRow, 'startDate' | 'dueDate'>,
  todos: readonly Todo[],
  today: string,
): { from: string; to: string } | null {
  const froms = [project.startDate, ...todos.map((t) => isoDayKey(t.createdAt))].filter((d): d is string => !!d);
  if (!froms.length) return null;
  const from = froms.reduce((a, b) => (b < a ? b : a));
  const to = [today, project.dueDate].filter((d): d is string => !!d).reduce((a, b) => (b > a ? b : a));
  return { from, to: to < from ? from : to };
}

/**
 * One point per day, inclusive, both series cumulative. Reconstructed from the to-dos that
 * exist NOW: a deleted to-do drops out of both lines retroactively and a re-opened one leaves
 * the completed line — there is no history table (a stated cost, not an oversight).
 */
export function burnupSeries(todos: readonly Todo[], from: string, to: string): BurnupPoint[] {
  const created = todos.map((t) => isoDayKey(t.createdAt));
  const completed = todos.flatMap((t) => (t.state === 'done' && t.completedAt ? [isoDayKey(t.completedAt)] : []));
  const out: BurnupPoint[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) {
    out.push({
      day,
      created: created.filter((d) => d <= day).length,
      completed: completed.filter((d) => d <= day).length,
    });
  }
  return out;
}

/** Tick days for the axis: every whole number of weeks from `from`, at most `maxTicks` of them. */
export function tickDays(from: string, to: string, maxTicks = 12): string[] {
  const total = Math.max(0, daysBetween(from, to));
  const step = Math.max(7, Math.ceil(total / maxTicks / 7) * 7);
  const out: string[] = [];
  for (let day = from; day <= to; day = addDays(day, step)) out.push(day);
  return out;
}

/** A y-axis top that reads cleanly: up to 4 as is (never below 1), then the next multiple of 5. */
export function niceMax(n: number): number {
  return n <= 4 ? Math.max(1, n) : Math.ceil(n / 5) * 5;
}
